/**
 * Preset API Implementation
 * 
 * Clean TypeScript abstraction for chord progression storage and retrieval.
 * Handles CRUD operations for user progressions with intelligent caching and
 * Supabase integration. Search functionality is provided via re-exports from
 * presetSearchAPI.ts for better code organization.
 * 
 * Core Features:
 * - Create, read, update, delete user progressions
 * - Intelligent caching with automatic invalidation
 * - Supabase integration with offline fallback
 * - Comprehensive error handling and validation
 * - Popular progressions for discovery
 * 
 * Search Features (Re-exported):
 * - Advanced hybrid search with filter detection
 * - Simple text-based search
 * - Multiple sorting and filtering options
 * - Relevance scoring and match explanations
 * 
 * @example
 * ```typescript
 * // Save a new progression
 * const result = await saveProgression("My Jazz Progression", progression);
 * 
 * // Search with natural language
 * const searchResults = await searchProgressions({
 *   query: "easy jazz guitar",
 *   limit: 20
 * });
 * 
 * // Get popular progressions for discovery
 * const popular = await getPopularProgressions();
 * ```
 */

import {
  Progression,
  ApiResponse,
  SaveProgressionResponse,
  LoadProgressionResponse,
  DeleteProgressionResponse,
  STORAGE_CONFIG
} from './presetSchema';

import { 
  saveUserProgression,
  deleteUserProgression
} from '../data/dataService';
import { 
  getAllCachedProgressions,
  invalidateUserProgressionsCache
} from '../data/cacheService';
import { getCurrentUser } from '../auth/supabaseAuthService';

// ================================================================================
// SEARCH API RE-EXPORTS
// ================================================================================

/**
 * Re-export all search functionality from dedicated search API
 * This keeps the API organized while providing a unified import point
 */
export {
  // Main search function (replaces both simple and advanced search)
  searchProgressions,
  
  // Utility functions
  detectFiltersFromQuery,
  getProgressionChords,
  sortProgressions,
  
  // TypeScript interfaces and types
  type SearchOptions,
  type ScoredProgression,
  type QuickFilterTab
} from './presetSearchAPI';

// ================================================================================
// UTILITY FUNCTIONS
// ================================================================================

/**
 * Create a standardized API response with timestamp
 * 
 * @template T - The type of data being returned
 * @param {boolean} success - Whether the operation was successful
 * @param {T} [data] - The response data (required for success)
 * @param {string} [error] - Error message (required for failure)
 * @returns {ApiResponse<T>} Standardized API response
 */
function createApiResponse<T>(success: true, data: T): ApiResponse<T>;
function createApiResponse<T>(success: false, data: undefined, error: string): ApiResponse<T>;
function createApiResponse<T>(success: boolean, data?: T, error?: string): ApiResponse<T> {
  return {
    success,
    data,
    error,
    timestamp: Date.now()
  } as ApiResponse<T>;
}

// ================================================================================
// PROGRESSION RETRIEVAL
// ================================================================================

/**
 * Get all available progressions (standard + user) with caching
 * 
 * Retrieves all progressions from both standard library and user-created
 * progressions, sorted alphabetically by name. Uses intelligent caching
 * for optimal performance.
 * 
 * @returns {Promise<ApiResponse<Progression[]>>} All available progressions
 * 
 * @example
 * ```typescript
 * const result = await getAllProgressions();
 * if (result.success) {
 *   console.log(`Found ${result.data.length} progressions`);
 * }
 * ```
 */
export async function getAllProgressions(): Promise<ApiResponse<Progression[]>> {
  try {
    const { standard, user } = await getAllCachedProgressions();
    const progressions = [...standard, ...user];
    
    // Sort by name for consistent ordering
    progressions.sort((a, b) => a.name.localeCompare(b.name));
    
    return createApiResponse(true, progressions);
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to load progressions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get user-created progressions with caching
 * 
 * @returns {Promise<ApiResponse<Progression[]>>} User-created progressions only
 */
export async function getUserProgressions(): Promise<ApiResponse<Progression[]>> {
  try {
    const { user } = await getAllCachedProgressions();
    return createApiResponse(true, user);
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to load user progressions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// ================================================================================
// PROGRESSION MANAGEMENT
// ================================================================================

/**
 * Save a new user progression
 * 
 * Creates or updates a user progression with proper validation and metadata.
 * Requires user authentication and automatically invalidates cache after save.
 * 
 * @param {string} name - Display name for the progression
 * @param {Progression} progression - The progression data to save
 * @param {object} [_options] - Save options (currently unused, reserved for future)
 * @returns {Promise<SaveProgressionResponse>} Save operation result
 * 
 * @example
 * ```typescript
 * const result = await saveProgression("My Jazz Progression", {
 *   arrangements: [...],
 *   metadata: { difficulty: "intermediate", genre: "jazz" }
 * });
 * 
 * if (result.success) {
 *   console.log("Progression saved successfully");
 * }
 * ```
 */
export async function saveProgression(
  name: string,
  progression: Progression,
  _options: { overwrite?: boolean } = {}
): Promise<SaveProgressionResponse> {
  try {
    // Validate input
    if (!name.trim()) {
      return createApiResponse(false, undefined, 'Progression name is required');
    }
    
    // Validate progression has arrangements
    if (!progression.arrangements || progression.arrangements.length === 0) {
      return createApiResponse(false, undefined, 'At least one guitar arrangement is required');
    }
    
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      return createApiResponse(false, undefined, 'User must be authenticated to save progressions');
    }
    
    // Create complete progression with updated metadata
    const now = Date.now();
    const completeProgression: Progression = {
      ...progression,
      name,
      metadata: {
        ...progression.metadata,
        modified: now,
        guitarCount: progression.arrangements.length,
        version: STORAGE_CONFIG.CURRENT_VERSION
      }
    };
    
    // Save to Supabase
    const result = await saveUserProgression(user.uid, completeProgression);
    if (result.success) {
      // Invalidate user progressions cache to ensure fresh data
      await invalidateUserProgressionsCache();
      return createApiResponse(true, completeProgression);
    } else {
      return createApiResponse(false, undefined, result.error || 'Failed to save progression');
    }
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to save progression: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Load a specific progression by ID or name
 * 
 * Attempts to load a progression from user presets first, then falls back
 * to searching all available progressions. Updates usage tracking for
 * found progressions.
 * 
 * @param {string} identifier - Progression ID or name to search for
 * @returns {Promise<LoadProgressionResponse>} Load operation result
 * 
 * @example
 * ```typescript
 * const result = await loadProgression("my-progression-id");
 * if (result.success) {
 *   const { progression, arrangements } = result.data;
 *   console.log(`Loaded: ${progression.name}`);
 * }
 * ```
 */
export async function loadProgression(identifier: string): Promise<LoadProgressionResponse> {
  try {
    // Try to load as user progression first (legacy localStorage support)
    const presetKey = STORAGE_CONFIG.PROGRESSION_PREFIX + identifier;
    const presetData = localStorage.getItem(presetKey);
    
    if (presetData) {
      const parsedData = JSON.parse(presetData);
      // Only accept valid data format
      if (parsedData.arrangements && parsedData.metadata && parsedData.source) {
        const progression = parsedData as Progression;
        
        // Update usage tracking
        progression.metadata.usageCount = (progression.metadata.usageCount || 0) + 1;
        progression.metadata.lastUsed = Date.now();
        
        return createApiResponse(true, {
          progression,
          arrangements: progression.arrangements || []
        });
      }
    }
    
    // Try to find in all available progressions
    const allProgressions = await getAllProgressions();
    if (allProgressions.success && allProgressions.data) {
      const progression = allProgressions.data.find(p => 
        p.id === identifier || p.name === identifier
      );
      
      if (progression) {
        return createApiResponse(true, {
          progression,
          arrangements: progression.arrangements || []
        });
      }
    }
    
    return createApiResponse(false, undefined, 'Progression not found');
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to load progression: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Delete a user progression
 * 
 * Removes a user-created progression from Supabase storage.
 * Requires user authentication and automatically invalidates cache after deletion.
 * 
 * @param {string} progressionId - ID of the progression to delete
 * @returns {Promise<DeleteProgressionResponse>} Delete operation result
 * 
 * @example
 * ```typescript
 * const result = await deleteProgression("progression-123");
 * if (result.success) {
 *   console.log("Progression deleted successfully");
 * }
 * ```
 */
export async function deleteProgression(progressionId: string): Promise<DeleteProgressionResponse> {
  try {
    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      return createApiResponse(false, undefined, 'User must be authenticated to delete progressions');
    }
    
    // Delete from Supabase
    const result = await deleteUserProgression(user.uid, progressionId);
    if (result.success) {
      // Invalidate user progressions cache to ensure fresh data
      await invalidateUserProgressionsCache();
      return createApiResponse(true, undefined);
    } else {
      return createApiResponse(false, undefined, result.error || 'Failed to delete progression');
    }
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to delete progression: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// ================================================================================
// DISCOVERY & POPULAR PROGRESSIONS
// ================================================================================

/**
 * Get popular progressions for discovery and empty search states
 * 
 * Returns a curated set of popular standard progressions and recently
 * modified user progressions. Used for the home screen and when users
 * haven't entered a search query.
 * 
 * @returns {Promise<ApiResponse<{standard: Progression[], user: Progression[]}>>} Popular progressions
 * 
 * @example
 * ```typescript
 * const result = await getPopularProgressions();
 * if (result.success) {
 *   const { standard, user } = result.data;
 *   console.log(`${standard.length} popular, ${user.length} recent user progressions`);
 * }
 * ```
 */
export async function getPopularProgressions(): Promise<ApiResponse<{ standard: Progression[], user: Progression[] }>> {
  try {
    const { standard, user } = await getAllCachedProgressions();
    
    // Get standard progressions (limit to 6 most popular)
    const standardProgressions = standard.slice(0, 6);
    
    // Get recent user progressions (limit to 4, sorted by modified date)
    const userProgressions = user
      .sort((a, b) => (b.metadata.modified || 0) - (a.metadata.modified || 0))
      .slice(0, 4);
    
    return createApiResponse(true, { standard: standardProgressions, user: userProgressions });
  } catch (error) {
    return createApiResponse(false, undefined, `Failed to get popular progressions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
